import { RulesTable } from '@kw/quokka-ui';
import { useMemo, useState } from 'react';

import { useLocalStorage } from '../../../utils/useLocalStorage';
import { getEvidencesCallbackWithToken } from './utils/dopplerUtil';
import { buildPolicyViolationMap, getReviewActionOptions } from './utils/helper';
import { useFetchRuleSummaries } from './utils/useFetchRuleSummaries';
import { GuardianReviewModal } from './GuardianReviewModal';
import { AuthPermissions } from '../../../auth/AuthPermissions';
import { verifyAuthPermission } from '../../../auth/VerifyAuthPermission';
import { OrgPolicyResults } from './types/ApplicationDetailsType';

const emptyHighRiskCountries: string[] = [];

export const GuardianRulesTable = function GuardianRulesTable({
  data,
  analysisId,
  customRules,
  highRiskCountries = emptyHighRiskCountries,
  refresh,
  filterSection,
  policyViolations,
}: {
  data: object;
  analysisId: string;
  customRules?: object;
  highRiskCountries?: string[];
  refresh?: () => void; // when we want to refresh the custom rules data after a review action
  filterSection?: 'overview' | 'permission' | 'behaviors' | 'malicious' | '';
  policyViolations: OrgPolicyResults[];
}) {
  const [accessToken = ''] = useLocalStorage('accessToken', '');

  const [selectedRule, setSelectedRule] = useState({});

  const { reloadRuleSummariesData } = useFetchRuleSummaries(analysisId, {
    suspense: false,
    revalidateOnMount: false,
    revalidateOnFocus: false,
  });

  const refreshRules = async (...args: any[]): Promise<void> => {
    reloadRuleSummariesData();
    if (refresh) refresh();
  };

  const [isRuleReviewModalOpen, setIsRuleReviewModalOpen] = useState(false);

  const canReview = verifyAuthPermission(AuthPermissions.REVIEW_APPLICATION);
  const ruleActionOptions = getReviewActionOptions(canReview, setIsRuleReviewModalOpen);

  // map of rule id to org policy results (multiple policies can reference the same rule)
  const policyViolationMap = useMemo(() => buildPolicyViolationMap(policyViolations), [policyViolations]);

  // memoize the RulesTable component to prevent re-rendering when the parent component re-renders because of search bar updates
  // especially since the RulesTable component is a heavy component in terms of rendering time
  return useMemo(
    () => (
      <>
        <GuardianReviewModal
          analysisId={analysisId}
          selectedItem={selectedRule}
          fetchData={refreshRules}
          reviewType="rule"
          setLoading={() => {}}
          afterSaveArgs={[]}
          isOpen={isRuleReviewModalOpen}
          setIsOpen={setIsRuleReviewModalOpen}
        />
        <RulesTable
          ruleSummaries={data}
          uuid={analysisId}
          getEvidencesCallback={getEvidencesCallbackWithToken(accessToken)}
          ruleActionOptions={ruleActionOptions}
          setSelectedRule={setSelectedRule}
          customRules={customRules}
          highRiskCountries={highRiskCountries}
          refresh={refresh}
          instance="QScout"
          searchConfig={['rule.shortDescription.text', 'rule.name']}
          filterSection={filterSection}
          policyViolations={policyViolationMap}
        />
      </>
    ),
    [data, analysisId, accessToken, ruleActionOptions, isRuleReviewModalOpen, customRules, highRiskCountries],
  );
};
